import { UUID } from '../utils/uuid';

type ISODateString = string;
type MarkdownEnabledContent = string;

type IChatRole = 'user' | 'assistant' | 'system';

export interface IChatMessage {
  id: UUID;
  createdAt: ISODateString;
  role: IChatRole;
  content: MarkdownEnabledContent;
  type: 'text';
  /** Welcome bubble: show context-aware action chips while this is the latest message. */
  isWelcome?: boolean;
}

// MARK: - Welcome actions (flat, context-aware; no multi-step graph)

export type WelcomeActionContext = {
  isModelPage: boolean;
  hasModelOutput: boolean;
};

export type WelcomeAction =
  | {
    id: string;
    kind: 'prompt';
    /** Chip label and message submitted to the real assistant (markdown). */
    prompt: string;
  }
  | {
    id: string;
    kind: 'canned';
    /** Chip label / user bubble text (markdown). */
    label: string;
    /** Fixed assistant reply (markdown). */
    reply: string;
  };

export const WELCOME_MESSAGE_CONTENT = `Hello! I'm ESET's AI Assistant.
How can I help you today? Choose an option below or tell me what you need.`;

const EDIT_MODEL_ACTION: WelcomeAction = {
  id: 'edit-model',
  kind: 'prompt',
  prompt: 'I want to evaluate an 8-hour 5 MW battery system with 80% round-trip efficiency and a 20-year lifetime.',
};


const TOP_THREE_TAKEAWAYS_ACTION: WelcomeAction = {
  id: 'top-three-takeaways',
  kind: 'prompt',
  prompt: 'What are the top three takeaways from this result?',
};

const GET_STARTED_ACTION: WelcomeAction = {
  id: 'get-started',
  kind: 'canned',
  label: 'How do I get started with ESET?',
  reply: `Here's a simple path to get started with ESET—the **Energy Storage Evaluation Tool**—PNNL's web suite for modeling, optimizing, and valuing energy storage systems.

**1. Sign in**
Create an account (or log in), then open your **dashboard**. That's your home base for all models and applications.

**2. Choose an application**
Pick the module that matches your technology or study goal—for example **BSET** (batteries), **HESET** (hydrogen), **MASCORE** (microgrids), **PSHET** (pumped hydro), **VBAT** (building flexibility), or **ES4R2** (resilience and reliability).

**3. Set up a model**
Create a new model, change settings and parameters, and upload any required input files. Hover over field names for help text, and use templates when bulk data is needed.

**4. Save and run**
**Save** your work anytime. When inputs look complete, click **Run**. Feel free to track the progress or come back later.

**5. Review results**
After the evaluation finishes, open **Output** for summaries, charts, and downloadable data.

I can walk you through any step, help you pick an application, or explain a specific setting. What would you like help with first?`,
};

const BEST_FIT_ACTION: WelcomeAction = {
  id: 'best-fit',
  kind: 'canned',
  label: 'Which ESET application best fits my needs?',
  reply: `I can help match you to the right ESET application. ESET covers several energy storage technologies—for example:

- **BSET** — battery energy storage systems
- **HESET** — hydrogen energy storage systems
- **MASCORE** — microgrid DER sizing for cost and resilience
- **PSHET** — pumped-storage hydropower
- **VBAT** — virtual battery flexibility from building loads
- **ES4R2** — energy storage for distribution resilience and end-user reliability

Tell me a bit about your project, such as:
- The storage technology or system type you have in mind
- Your main goal (sizing, valuation, stacked services, resilience, etc.)
- Grid services or use cases you care about

I'll recommend the best fit and explain why.`,
};

const APPLICATION_DEEP_DIVE_ACTION: WelcomeAction = {
  id: 'application-deep-dive',
  kind: 'canned',
  label: 'Tell me more about an ESET application',
  reply: `Happy to dig into a specific ESET application. Which one would you like to learn more about?

- **BSET** — Battery Energy Storage Evaluation Tool
- **HESET** — Hydrogen Energy Storage Evaluation Tool
- **MASCORE** — Microgrid Asset Sizing considering Cost and Resilience
- **PSHET** — Pumped-Storage Hydropower Evaluation Tool
- **VBAT** — Virtual Battery Assessment Tool
- **ES4R2** — Energy Storage for Resilience and Reliability

Name the application (or describe what it does), and I can cover capabilities, typical inputs, outputs, and when to use it.`,
};

/**
 * Resolve welcome chips from current assistant context.
 * Context-specific shortcuts first, then general guides.
 */
export function resolveWelcomeActions(ctx: WelcomeActionContext): WelcomeAction[] {
  const actions: WelcomeAction[] = [];
  if (ctx.isModelPage) {
    if (ctx.hasModelOutput) {
      actions.push(TOP_THREE_TAKEAWAYS_ACTION);
    }

    actions.push(EDIT_MODEL_ACTION);
  } else {
    actions.push(GET_STARTED_ACTION, BEST_FIT_ACTION, APPLICATION_DEEP_DIVE_ACTION);
  }
  return actions;
}
